import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaClock, FaCheckCircle, FaSpinner, FaDownload, FaUpload, FaThumbsUp, FaTimes } from 'react-icons/fa';
import { offerAPI } from '../../services/offerService';
import api from '../../services/api';

const OrderDetails = ({ userRole }) => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [deliveryFiles, setDeliveryFiles] = useState([]);
  const [delivering, setDelivering] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [offerId]);

  // Poll for order status changes every 10 seconds when in delivered status
  useEffect(() => {
    if (order?.status === 'delivered' && userRole === 'buyer') {
      const interval = setInterval(fetchOrder, 10000);
      return () => clearInterval(interval);
    }
  }, [order?.status, userRole]);

  const fetchOrder = async () => {
    try {
      console.log('📦 Fetching order:', offerId);
      const response = await offerAPI.getOfferById(offerId);
      console.log('📦 Order data received:', response);
      
      let orderData = response;
      if (response && response.data) {
        orderData = response.data;
      } else if (response && response.offer) {
        orderData = response.offer;
      }
      
      setOrder(orderData);
      calculateTimeLeft(orderData);
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching order:', error);
      if (error.response?.status === 401) {
        navigate('/signin');
      }
      setLoading(false);
    }
  };

  const calculateTimeLeft = (orderData = order) => {
    if (!orderData || !orderData.startedAt) return;
    
    const deadline = new Date(orderData.startedAt);
    deadline.setDate(deadline.getDate() + (orderData.deliveryTime || orderData.timeline || 7));
    const now = new Date();
    const difference = deadline - now;
    
    if (difference <= 0) {
      setTimeLeft({ expired: true });
      return;
    }
    
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    
    setTimeLeft({ days, hours, minutes, expired: false });
  };

  const handleStartWork = async () => {
    try {
      await api.patch(`/offers/${offerId}/status`, { status: 'in_progress', startedAt: new Date() });
      fetchOrder();
    } catch (error) {
      console.error('Error starting work:', error);
      alert('Failed to start work');
    }
  };

  const handleDeliverOrder = async () => {
    setDelivering(true);
    try {
      const formData = new FormData();
      formData.append('message', deliveryMessage);
      deliveryFiles.forEach(file => {
        formData.append('files', file);
      });
      
      await api.post(`/offers/${offerId}/deliver`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      fetchOrder();
      setDeliveryMessage('');
      setDeliveryFiles([]);
      alert('Order delivered successfully! The buyer will now review your work.');
    } catch (error) {
      console.error('Error delivering order:', error);
      alert('Failed to deliver order');
    } finally {
      setDelivering(false);
    }
  };

  const handleApproveOrder = async () => {
    if (!window.confirm('Approve this delivery? You will be redirected to pay the remaining 50% to release payment to the seller (80% to seller, 20% platform fee).')) {
      return;
    }
    
    setApproving(true);
    try {
      const response = await api.post(`/offers/${offerId}/approve`);
      console.log('Approval response:', response.data);
      
      if (response.data.success && response.data.paymentSession?.url) {
        // Redirect to Stripe checkout for milestone 2 (50% completion)
        alert(`Please complete the final 50% payment to release funds to the seller.`);
        window.location.href = response.data.paymentSession.url;
      } else if (response.data.success) {
        // Fallback: order completed without payment (shouldn't happen with 50/50 flow)
        alert(`Order completed!`);
        fetchOrder();
      } else {
        alert('Failed to approve order: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error approving order:', error);
      alert('Failed to approve order: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!rejectMessage.trim()) {
      alert('Please provide feedback on what changes are needed');
      return;
    }
    
    try {
      await api.post(`/offers/${offerId}/request-changes`, { message: rejectMessage });
      alert('Change request sent to seller. The order status has been set back to in_progress.');
      setShowRejectModal(false);
      setRejectMessage('');
      fetchOrder();
    } catch (error) {
      console.error('Error requesting changes:', error);
      alert('Failed to request changes: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">Order not found</div>
      </div>
    );
  }

  const canStartWork = userRole === 'seller' && order.status === 'accepted';
  const canDeliver = userRole === 'seller' && order.status === 'in_progress';
  const canApprove = userRole === 'buyer' && order.status === 'delivered';
  const isCompleted = order.status === 'completed';
  const isDelivered = order.status === 'delivered';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-4">{order.title || 'Order Details'}</h1>
        
        {/* Status Badge */}
        <div className="mb-6">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold
            ${order.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : ''}
            ${order.status === 'delivered' ? 'bg-purple-100 text-purple-800' : ''}
            ${order.status === 'completed' ? 'bg-green-100 text-green-800' : ''}
            ${order.status === 'accepted' ? 'bg-yellow-100 text-yellow-800' : ''}
          `}>
            {order?.status?.toUpperCase() || "PENDING"}
          </span>
        </div>

        {/* Countdown Timer */}
        {order.status === 'in_progress' && order.startedAt && timeLeft && !timeLeft.expired && (
          <div className="bg-gray-100 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <FaClock className="text-[#708238]" /> Time Remaining
            </h3>
            <div className="text-2xl font-bold text-[#708238]">
              {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Delivery by: {new Date(new Date(order.startedAt).setDate(new Date(order.startedAt).getDate() + (order.deliveryTime || order.timeline || 7))).toLocaleDateString()}
            </p>
          </div>
        )}

        {/* Order Details */}
        <div className="space-y-4 mb-6">
          <div>
            <h3 className="font-semibold">Description</h3>
            <p className="text-gray-700">{order.description || order.scope || 'No description provided'}</p>
          </div>
          <div>
            <h3 className="font-semibold">Price</h3>
            <p className="text-2xl font-bold text-[#708238]">${order.price || order.amount || 0}</p>
            {isCompleted && (
              <p className="text-sm text-gray-500 mt-1">
                Platform fee (20%): ${(order.price * 0.2).toFixed(2)} | Seller receives: ${(order.price * 0.8).toFixed(2)}
              </p>
            )}
          </div>
          <div>
            <h3 className="font-semibold">Delivery Time</h3>
            <p>{order.deliveryTime || order.timeline || 'Not specified'} days</p>
          </div>
        </div>

        {/* Delivery Information */}
        {order.deliveredWork && order.deliveredWork.message && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2">Delivery Message</h3>
            <p className="text-gray-700">{order.deliveredWork.message}</p>
            {order.deliveredWork.files && order.deliveredWork.files.length > 0 && (
              <div className="mt-3">
                <h4 className="font-medium text-sm mb-2">Attached Files:</h4>
                {order.deliveredWork.files.map((file, idx) => (
                  <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:underline block text-sm">
                    📎 {file.filename}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {canStartWork && (
          <button
            onClick={handleStartWork}
            className="w-full bg-[#708238] text-white py-3 rounded-lg font-semibold hover:bg-[#5a6a2d] transition mb-4"
          >
            Start Work
          </button>
        )}

        {canDeliver && (
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold mb-3">Deliver Order</h3>
            <textarea
              value={deliveryMessage}
              onChange={(e) => setDeliveryMessage(e.target.value)}
              placeholder="Add a delivery message..."
              className="w-full p-3 border rounded-lg mb-3"
              rows="3"
            />
            <div className="mb-3">
              <label className="block text-sm font-medium mb-2">Attach Files</label>
              <input
                type="file"
                multiple
                onChange={(e) => setDeliveryFiles(Array.from(e.target.files))}
                className="w-full"
              />
            </div>
            <button
              onClick={handleDeliverOrder}
              disabled={delivering}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition"
            >
              {delivering ? 'Delivering...' : 'Deliver Order'}
            </button>
          </div>
        )}

        {/* Approve/Reject Buttons for Buyer */}
        {canApprove && (
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold mb-3 text-lg">Review Delivery</h3>
            <p className="text-gray-600 mb-4">
              The seller has delivered the work. Please review and either approve (release payment) or request changes.
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleApproveOrder}
                disabled={approving}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2"
              >
                <FaThumbsUp /> {approving ? 'Processing...' : 'Approve & Release Payment'}
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex-1 bg-yellow-600 text-white py-3 rounded-lg font-semibold hover:bg-yellow-700 transition flex items-center justify-center gap-2"
              >
                <FaTimes /> Request Changes
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Upon approval: 80% (${(order.price * 0.8).toFixed(2)}) goes to seller, 20% (${(order.price * 0.2).toFixed(2)}) platform fee
            </p>
          </div>
        )}

        {isCompleted && (
          <div className="border-t pt-4 mt-4 bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-700">
              <FaCheckCircle /> 
              <span className="font-semibold">Order Completed!</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Payment has been released to the seller. Thank you for using our platform!
            </p>
          </div>
        )}

        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-gray-600 hover:text-gray-800 transition"
        >
          ← Back
        </button>
      </div>

      {/* Request Changes Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Request Changes</h3>
            <p className="text-gray-600 mb-4">
              Please provide feedback on what changes you need from the seller:
            </p>
            <textarea
              value={rejectMessage}
              onChange={(e) => setRejectMessage(e.target.value)}
              placeholder="Describe the changes needed..."
              className="w-full p-3 border rounded-lg mb-4"
              rows="4"
            />
            <div className="flex gap-3">
              <button
                onClick={handleRequestChanges}
                className="flex-1 bg-yellow-600 text-white py-2 rounded-lg font-semibold hover:bg-yellow-700"
              >
                Submit Request
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectMessage('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
